"use client";

import { useState, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import useAllLeagues from "@/hooks/useAllLeagues";
import ListIcon from "@/assets/images/v21-list.svg";
import ChevronDownIcon from "@/assets/images/v21-chevron-down.svg";
import SearchIcon from "@/assets/images/v21-search.svg";

export default function LeagueFilterV21({
  selectedIds = [],
  onChange,
  className = "",
  anchorRef,
}) {
  const { leagues, loading } = useAllLeagues();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [tempSelectedIds, setTempSelectedIds] = useState(selectedIds || []);
  const [viewMode, setViewMode] = useState("league");
  const triggerRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    setTempSelectedIds(selectedIds || []);
  }, [selectedIds]);

  const updateDropdownPosition = () => {
    if (!triggerRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const gap = 8;
    const padding = 8;
    let left;
    let width;
    if (anchorRef?.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      left = anchorRect.left + 10;
      width = Math.min(anchorRect.width * 0.95, 400);
    } else {
      left = triggerRect.left;
      width = Math.min(480, window.innerWidth - left - padding);
    }
    setDropdownStyle({
      position: "fixed",
      top: triggerRect.bottom + gap,
      left,
      width,
      zIndex: 99999,
    });
  };

  useLayoutEffect(() => {
    if (open && triggerRef.current) updateDropdownPosition();
  }, [open]);

  const normalizedLeagues = useMemo(
    () =>
      (leagues || []).map((l) => ({
        ...l,
        country: l.country || l.countryName || "Khác",
      })),
    [leagues],
  );

  const filteredLeagues = useMemo(() => {
    if (!keyword.trim()) return normalizedLeagues || [];
    const lower = keyword.toLowerCase();
    return (normalizedLeagues || []).filter((league) => {
      const nameVN = (league.nameVN || "").toLowerCase();
      const name = (league.name || "").toLowerCase();
      const country = (league.country || "").toLowerCase();
      return (
        nameVN.includes(lower) || name.includes(lower) || country.includes(lower)
      );
    });
  }, [normalizedLeagues, keyword]);

  const leaguesByCountry = useMemo(() => {
    const groups = {};
    for (const league of filteredLeagues) {
      const countryKey = league.country || "Khác";
      if (!groups[countryKey]) {
        groups[countryKey] = {
          country: countryKey,
          countryLogo: league.countryLogo || null,
          items: [],
        };
      }
      groups[countryKey].items.push(league);
    }
    return Object.values(groups);
  }, [filteredLeagues]);

  const handleToggleLeague = (id) => {
    setTempSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    const allIds = (filteredLeagues || []).map((l) => l.id);
    setTempSelectedIds(allIds);
  };

  const handleClearAll = () => {
    setTempSelectedIds([]);
  };

  const handleConfirm = () => {
    onChange?.(tempSelectedIds);
    setOpen(false);
  };

  const hasSelection = selectedIds && selectedIds.length > 0;
  const triggerLabel = hasSelection
    ? `${selectedIds.length} giải đấu`
    : "Tất cả giải đấu";

  return (
    <div className={`relative block lg:inline-block min-w-0 ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setOpen((prev) => {
            if (prev) return false;
            updateDropdownPosition();
            return true;
          });
        }}
        className="flex items-center gap-1 lg:gap-2 px-1.5 py-2 lg:px-3 lg:py-[9px] bg-white border [border-style:solid] border-[#E5E7EB] rounded-[6px] text-[#374151] text-[11px] lg:text-[13px] font-semibold transition-all hover:border-gray-300 w-full min-w-0 lg:min-w-[160px] appearance-none outline-none box-border"
      >
        <div className="flex shrink-0 items-center justify-center scale-90 lg:scale-100">
            <img src={ListIcon} alt="list" className="w-[18px] h-[18px]" />
        </div>
        <span className="flex-1 text-left truncate">{triggerLabel}</span>
        <img 
            src={ChevronDownIcon} 
            alt="arrow" 
            className={`w-3 h-3 shrink-0 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            style={dropdownStyle}
            className="max-h-[60vh] flex flex-col rounded-xl bg-white text-gray-900 shadow-2xl border border-gray-200 md:w-[550px] box-border"
          >
            <div className="flex items-center justify-between px-4 pt-3 pb-2 shrink-0">
              <div className="relative flex-1 min-w-0">
                <input
                  type="text"
                  placeholder="Tìm tên giải đấu"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="w-full rounded-lg border [border-style:solid] border-gray-200 bg-gray-50 py-2 pl-3 pr-10 text-sm outline-none focus:border-blue-500 focus:bg-white transition-all font-semibold appearance-none box-border"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <img src={SearchIcon} alt="search" className="w-4 h-4 opacity-50" />
                </span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="ml-3 p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors appearance-none outline-none border-none bg-transparent"
              >
                ✕
              </button>
            </div>

            <div className="px-4 pb-3 border-b border-gray-100 shrink-0">
              <div className="flex items-center justify-between text-xs uppercase tracking-wider">
                <span className="font-bold text-gray-500">Xem theo</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setViewMode("league")}
                    className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all appearance-none outline-none ${viewMode === "league"
                        ? "bg-blue-50 text-blue-600 border [border-style:solid] border-blue-200"
                        : "bg-gray-50 text-gray-500 border [border-style:solid] border-gray-200 hover:bg-gray-100"
                      }`}
                  >
                    Giải đấu
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("country")}
                    className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all appearance-none outline-none ${viewMode === "country"
                        ? "bg-blue-50 text-blue-600 border [border-style:solid] border-blue-200"
                        : "bg-gray-50 text-gray-500 border [border-style:solid] border-gray-200 hover:bg-gray-100"
                      }`}
                  >
                    Quốc gia
                  </button>
                </div>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-auto py-3 px-4">
              {loading ? (
                <div className="py-12 text-center text-sm text-gray-400 font-bold">
                  Đang tải danh sách giải đấu...
                </div>
              ) : filteredLeagues.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-400 font-bold">
                  Không tìm thấy giải đấu nào
                </div>
              ) : viewMode === "league" ? (
                <div className="grid grid-cols-2 gap-2">
                  {filteredLeagues.map((league) => {
                    const checked = tempSelectedIds.includes(league.id);
                    const displayName = league.nameVN || league.name || "";
                    return (
                      <button
                        key={league.id}
                        type="button"
                        onClick={() => handleToggleLeague(league.id)}
                        className={`flex items-center gap-2.5 rounded-lg border [border-style:solid] p-2 text-xs text-left transition-all min-w-0 hover:shadow-sm appearance-none outline-none ${
                            checked 
                            ? "border-blue-200 bg-blue-50/50" 
                            : "border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <span
                          className={`flex shrink-0 items-center justify-center w-4 h-4 rounded border text-[10px] font-bold transition-colors
                          ${checked
                              ? "bg-blue-600 border-blue-600 text-white"
                              : "border-gray-300 bg-white"
                            }
                        `}
                        >
                          {checked ? "✓" : ""}
                        </span>
                        {league.logo && (
                          <img
                            src={league.logo}
                            alt=""
                            className="w-5 h-5 object-contain shrink-0"
                          />
                        )}
                        <span className={`min-w-0 truncate font-bold ${checked ? "text-blue-700" : "text-gray-700"}`} title={league.nameVN || league.name}>
                          {displayName}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col gap-6">
                  {leaguesByCountry.map((group) => (
                    <div key={group.country}>
                      <div className="flex items-center gap-2 mb-2 text-[11px] font-bold uppercase tracking-widest text-gray-400">
                        {group.countryLogo && (
                          <img
                            src={group.countryLogo}
                            alt={group.country}
                            className="w-4 h-4 object-contain grayscale opacity-70"
                          />
                        )}
                        <span>{group.country}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {group.items.map((league) => {
                          const checked = tempSelectedIds.includes(league.id);
                          const displayName = league.nameVN || league.name || "";
                          return (
                            <button
                              key={league.id}
                              type="button"
                              onClick={() => handleToggleLeague(league.id)}
                              className={`flex items-center gap-2.5 rounded-lg border [border-style:solid] p-2 text-xs text-left transition-all min-w-0 hover:shadow-sm appearance-none outline-none box-border ${
                                checked 
                                ? "border-blue-200 bg-blue-50/50" 
                                : "border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50"
                            }`}
                            >
                              <span
                                className={`flex shrink-0 items-center justify-center w-4 h-4 rounded border text-[10px] font-bold transition-colors
                                ${checked
                                    ? "bg-blue-600 border-blue-600 text-white"
                                    : "border-gray-300 bg-white"
                                  }
                              `}
                              >
                                {checked ? "✓" : ""}
                              </span>
                              {league.logo && (
                                <img
                                  src={league.logo}
                                  alt=""
                                  className="w-5 h-5 object-contain shrink-0"
                                />
                              )}
                              <span className={`min-w-0 truncate font-bold ${checked ? "text-blue-700" : "text-gray-700"}`} title={league.nameVN || league.name}>
                                {displayName}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-gray-100 bg-gray-50/50 shrink-0 rounded-b-xl">
              <div className="flex items-center gap-5">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="flex items-center gap-2 text-xs font-bold text-gray-600 hover:text-blue-600 transition-colors appearance-none outline-none border-none bg-transparent"
                >
                  <span
                    className={`flex shrink-0 items-center justify-center w-4 h-4 rounded border text-[10px] font-bold transition-colors ${filteredLeagues.length > 0 && filteredLeagues.every((l) => tempSelectedIds.includes(l.id))
                        ? "bg-blue-600 border-blue-600 text-white"
                        : "border-gray-300 bg-white"
                      }`}
                  >
                    {filteredLeagues.length > 0 && filteredLeagues.every((l) => tempSelectedIds.includes(l.id)) ? "✓" : ""}
                  </span>
                  Chọn tất cả
                </button>
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="flex items-center gap-2 text-xs font-bold text-gray-600 hover:text-blue-600 transition-colors appearance-none outline-none border-none bg-transparent"
                >
                  <span
                    className={`flex shrink-0 items-center justify-center w-4 h-4 rounded border text-[10px] font-bold transition-colors ${tempSelectedIds.length === 0 ? "bg-blue-600 border-blue-600 text-white" : "border-gray-300 bg-white"
                      }`}
                  >
                    {tempSelectedIds.length === 0 ? "✓" : ""}
                  </span>
                  Bỏ chọn tất cả
                </button>
              </div>
              <button
                type="button"
                onClick={handleConfirm}
                className="shrink-0 rounded-lg bg-blue-600 px-6 py-2 text-xs font-bold text-white shadow-sm hover:bg-blue-700 hover:shadow-md active:scale-95 transition-all appearance-none outline-none border-none box-border"
              >
                Xác nhận
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
